import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, MessageSquare, Mic, Users, Bookmark, User, Settings, MoreHorizontal, Search, PlusCircle, ArrowRight, Zap, RefreshCw, LogOut, Bell, BadgeCheck, Sparkles, Plus } from 'lucide-react';
import { auth, db, analytics } from '../../firebase';
import { logEvent } from 'firebase/analytics';
import { doc, getDoc, collection, query, where, onSnapshot, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import sparkLogo from '../../assets/spark-logo.png';
import { useIdentity } from '../../context/IdentityContext';
import { getAnonymousName, getAvatarForShadow } from '../../utils/shadowIdentity';
import { PremiumModal } from '../Premium/PremiumModal';
import { PremiumWelcomeModal } from '../Premium/PremiumWelcomeModal';
import { ServiceModal } from '../ServiceModal';
import { RightPanelProvider, useRightPanel } from '../../context/RightPanelContext';

export const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { identityMode, toggleIdentity } = useIdentity();
  
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [activeServiceMode, setActiveServiceMode] = useState(null); // 'TERMINATED' or null
  
  const [showAppModal, setShowAppModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: 'Get the App', description: 'This feature is fully unlocked on the Spark mobile app. Download it now to get the full experience!' });
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showVoiceMenu, setShowVoiceMenu] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  const [isBottomBarVisible, setIsBottomBarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScrollDirection = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsBottomBarVisible(false);
      } else {
        setIsBottomBarVisible(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScrollDirection, { passive: true });
    return () => window.removeEventListener('scroll', handleScrollDirection);
  }, [lastScrollY]);

  const shadowName = auth.currentUser ? getAnonymousName(auth.currentUser.uid) : 'Anonymous';
  const shadowAvatar = getAvatarForShadow(shadowName);
  
  const [isSwitching, setIsSwitching] = useState(false);
  const [activeCustomRooms, setActiveCustomRooms] = useState([]);

  useEffect(() => {
    if (!showVoiceMenu) return;
    const handleScroll = () => {
      setShowVoiceMenu(false);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('touchmove', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
    };
  }, [showVoiceMenu]);

  useEffect(() => {
    const q = query(
      collection(db, 'customVoiceRooms'),
      where('isOpen', '==', true)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const activeRooms = [];
      snapshot.docs.forEach(doc => {
        activeRooms.push({ id: doc.id, ...doc.data() });
      });
      activeRooms.sort((a, b) => {
        const tA = a.createdAt?.toMillis() || 0;
        const tB = b.createdAt?.toMillis() || 0;
        return tB - tA;
      });
      setActiveCustomRooms(activeRooms.slice(0, 3));
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let unsubscribeUser = null;
    
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        const currentTarget = location.pathname + location.search;
        if (currentTarget && currentTarget !== '/' && currentTarget !== '/login') {
          navigate(`/login?redirect=${encodeURIComponent(currentTarget)}`, { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
        if (unsubscribeUser) unsubscribeUser();
        return;
      }
      
      unsubscribeUser = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserProfile(data);
          
          if (data.isTerminated === true) {
            setActiveServiceMode('TERMINATED');
          }
          
          if (data.isPremium && !data.welcomeShown) {
             setShowWelcomeModal(true);
          }
        }
        setLoading(false);
      }, (error) => {
        console.error("Error fetching user profile:", error);
        setLoading(false);
      });
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUser) unsubscribeUser();
    };
  }, [navigate]);

  const handleCloseWelcomeModal = async () => {
    setShowWelcomeModal(false);
    if (auth.currentUser) {
      try {
        await updateDoc(doc(db, 'users', auth.currentUser.uid), { welcomeShown: true });
        setUserProfile(prev => ({ ...prev, welcomeShown: true }));
      } catch (e) {
        console.error("Failed to update welcomeShown status:", e);
      }
    }
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const q = query(
      collection(db, 'anonymousChats'),
      where('participants', 'array-contains', user.uid),
      where('saved', '==', true)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let count = 0;
      snapshot.docs.forEach(chatDoc => {
        const data = chatDoc.data();
        const isUser1 = data.user1 === user.uid;
        const myUnread = isUser1 ? (data.unreadCount_user1 || 0) : (data.unreadCount_user2 || 0);
        if (myUnread > 0) count++;
      });
      setUnreadTotal(count);
    });
    return () => unsubscribe();
  }, [userProfile]);

  // Fetch unread notifications
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    
    const notifQuery = query(
      collection(db, 'users', user.uid, 'notifications'),
      where('read', '==', false)
    );
    const unsubscribe = onSnapshot(notifQuery, (snapshot) => {
      setUnreadNotifications(snapshot.size);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) { }
  };

  const handleFeatureClick = (feature) => {
    if (feature === 'settings') {
      navigate('/settings');
      return;
    }
    if (feature === 'profile') {
      if (feature === 'profile' && identityMode === 'shadow') {
        navigate('/shadow-profile');
        return;
      }
      setModalConfig({
        title: 'Get the App',
        description: 'This feature is fully unlocked on the Spark mobile app. Download it now to get the full experience!'
      });
      setShowAppModal(true);
      return;
    }
    logEvent(analytics, 'sidebar_clicked', { feature: feature });
    if (feature === 'chat') navigate('/chat');
    else if (feature === 'voice1v1') navigate('/mode-select', { state: { featureType: 'voice1v1' } });
    else if (feature === 'voice4v4') navigate('/mode-select', { state: { featureType: 'voice4v4' } });
    else if (feature === 'customVoice') navigate('/custom-rooms');
    else if (feature === 'notifications') navigate('/notifications');
    else if (feature === 'saved') {
      if (identityMode === 'real') navigate('/saved-posts');
      else navigate('/saved-chats');
    }
    else if (feature === 'home') navigate('/');
  };

  const handleToggleIdentity = () => {
    toggleIdentity();
  };

  const isHome = location.pathname === '/';
  const currentPath = location.pathname;
  const isChatPage = currentPath.includes('/chat');
  const isSettings = currentPath.includes('/settings');
  const isProfile = currentPath.includes('/shadow-profile');

  const activeCallRoutes = ['/voice1v1/room', '/voice4v4/room', '/custom-rooms/room', '/voice-chat', '/group-voice-room', '/custom-voice-room'];
  const isActiveCall = activeCallRoutes.some(route => currentPath.includes(route));

  const allowedBottomBarPaths = [
    '/',
    '/chat',
    '/chat/',
    '/shadow-profile',
    '/shadow-profile/',
    '/mode-select',
    '/mode-select/',
    '/group-voice-setup',
    '/group-voice-setup/'
  ];

  const hideBottomBar = !allowedBottomBarPaths.includes(currentPath);

  const showDefaultRightPanel = !isActiveCall && !isChatPage && !isSettings;

  const NavItem = ({ icon: Icon, text, onClick, active, badge, badgeColor = "bg-[#0BD9E5] text-black" }) => (
    <button 
      onClick={onClick}
      className={`flex items-center gap-4 w-fit xl:w-full p-3 xl:px-4 xl:py-3 rounded-full hover:bg-white/10 transition-colors group ${active ? 'font-bold' : 'font-medium'}`}
    >
      <div className="relative shrink-0">
        <Icon size={26} className={active ? 'text-white' : 'text-gray-300 group-hover:text-white'} strokeWidth={active ? 2.5 : 2} />
        {badge > 0 && (
          <span className={`absolute -top-1 -right-1 w-4 h-4 ${badgeColor} text-[10px] font-bold rounded-full flex items-center justify-center`}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <span className={`hidden xl:block text-xl tracking-wide ${active ? 'text-white' : 'text-gray-300 group-hover:text-white'}`}>
        {text}
      </span>
    </button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-t-2 border-[#0BD9E5] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex justify-center font-poppins selection:bg-[#0BD9E5]/30">
      
      {/* Switch Animation Overlay */}
      <AnimatePresence>
        {isSwitching && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[200] bg-black flex flex-col items-center justify-center"
          >
             <motion.div 
               animate={{ rotate: 360 }} 
               transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
             >
                <RefreshCw size={48} className={identityMode === 'shadow' ? 'text-purple-500' : 'text-[#0BD9E5]'} />
             </motion.div>
             <motion.h2 
               initial={{ y: 20, opacity: 0 }}
               animate={{ y: 0, opacity: 1 }}
               className="mt-6 text-2xl font-bold"
             >
               Switching to {identityMode === 'shadow' ? 'Shadow' : 'Real'} Identity...
             </motion.h2>
          </motion.div>
        )}
      </AnimatePresence>

      {/* DESKTOP LEFT SIDEBAR */}
      <aside className="hidden sm:flex flex-col w-[80px] xl:w-[275px] h-screen sticky top-0 border-r border-gray-800 px-2 xl:px-4 py-3 shrink-0 z-30">
        
        {/* Logo */}
        <div 
          onClick={() => navigate('/')}
          className="p-3 xl:p-4 hover:bg-white/10 rounded-full cursor-pointer w-fit transition-colors mb-2"
        >
           <img src={sparkLogo} alt="Spark" className="h-8 xl:h-10 w-auto object-contain" />
        </div>

        {/* Navigation Items */}
        <div className="flex flex-col gap-1.5 mt-2">
          <NavItem 
            icon={Home} 
            text="Home" 
            active={isHome} 
            onClick={() => handleFeatureClick('home')} 
          />

          <NavItem 
            icon={MessageSquare} 
            text="Chat" 
            active={currentPath.includes('/chat')} 
            onClick={() => handleFeatureClick('chat')} 
            badge={unreadTotal}
          />

          {/* Voices Item with Inline Accordion (Desktop) */}
          <div 
            className="w-full flex flex-col"
            onMouseEnter={() => setShowVoiceMenu(true)}
            onMouseLeave={() => setShowVoiceMenu(false)}
          >
            <NavItem 
              icon={Mic} 
              text="Voices"
              active={currentPath.includes('/voice1v1') || currentPath.includes('/voice4v4') || currentPath.includes('/custom-rooms')} 
              onClick={() => setShowVoiceMenu(!showVoiceMenu)} 
            />
            <AnimatePresence>
              {showVoiceMenu && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }} 
                  animate={{ height: 'auto', opacity: 1 }} 
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden flex flex-col pl-4 xl:pl-6 space-y-1 my-1"
                >
                  <button 
                    onClick={() => { setShowVoiceMenu(false); toast.info("Coming soon!"); }} 
                    className="w-full p-2.5 rounded-xl hover:bg-white/10 transition-colors flex items-center justify-between font-bold text-white text-sm group"
                  >
                    <div className="flex items-center gap-3">
                      <Mic size={16} className="text-[#0BD9E5] group-hover:scale-110 transition-transform shrink-0" />
                      <span className="hidden xl:inline truncate">1-1 Room</span>
                    </div>
                    <Sparkles size={12} className="text-[#0BD9E5] hidden xl:inline shrink-0" />
                  </button>
                  
                  <button 
                    onClick={() => { setShowVoiceMenu(false); toast.info("Coming soon!"); }} 
                    className="w-full p-2.5 rounded-xl hover:bg-white/10 transition-colors flex items-center justify-between font-bold text-white text-sm group"
                  >
                    <div className="flex items-center gap-3">
                      <Users size={16} className="text-[#0BD9E5] group-hover:scale-110 transition-transform shrink-0" />
                      <span className="hidden xl:inline truncate">4-4 Room</span>
                    </div>
                    <Sparkles size={12} className="text-[#0BD9E5] hidden xl:inline shrink-0" />
                  </button>

                  <button 
                    onClick={() => { setShowVoiceMenu(false); handleFeatureClick('customVoice'); }} 
                    className="w-full p-2.5 rounded-xl hover:bg-white/10 transition-colors flex items-center gap-3 font-bold text-white text-sm group"
                  >
                    <PlusCircle size={16} className="text-[#0BD9E5] group-hover:scale-110 transition-transform shrink-0" />
                    <span className="hidden xl:inline truncate">Custom Voice</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <NavItem icon={Bell} text="Notifications" active={currentPath.includes('/notifications')} onClick={() => handleFeatureClick('notifications')} badge={unreadNotifications} badgeColor="bg-red-500 text-white" />
          <NavItem icon={User} text="Profile" onClick={() => handleFeatureClick('profile')} />
          <NavItem icon={Settings} text="Settings" onClick={() => handleFeatureClick('settings')} />
          
          <button 
            onClick={() => navigate('/create-shadow')}
            className="mt-4 bg-[#0BD9E5] hover:bg-[#09bcc7] text-black font-bold text-lg rounded-full w-12 h-12 xl:w-full xl:h-14 flex items-center justify-center transition-colors shadow-lg"
          >
             <span className="hidden xl:block">Post</span>
             <Zap size={24} className="xl:hidden" />
          </button>
        </div>

        {/* Profile Area */}
        <div className="mt-auto w-full relative">
           <AnimatePresence>
              {showProfileMenu && (
                 <motion.div 
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                    className="absolute bottom-full mb-2 right-0 xl:left-0 bg-black border border-gray-800 rounded-2xl shadow-xl shadow-black/50 py-2 w-56 z-50 overflow-hidden"
                 >
                    <button 
                      onClick={handleLogout}
                      className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors flex items-center gap-3 font-bold text-white text-[15px]"
                    >
                      <LogOut size={18} /> Log out @{userProfile?.username || 'user'}
                    </button>
                 </motion.div>
              )}
           </AnimatePresence>

           <div className="flex items-center justify-center xl:justify-between p-3 hover:bg-white/10 rounded-full transition-colors w-full relative">
              <div className="flex items-center gap-3 cursor-pointer overflow-hidden" onClick={() => handleFeatureClick('profile')}>
                 <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center shrink-0 border border-gray-700 overflow-hidden">
                    <img src={shadowAvatar} alt={shadowName} className="w-full h-full object-cover" />
                 </div>
                 <div className="hidden xl:flex flex-col min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-white text-[15px] truncate">{shadowName}</span>
                      {userProfile?.isPremium && <Sparkles size={14} className="text-[#0BD9E5] shrink-0" />}
                    </div>
                 </div>
              </div>

              <div className="hidden xl:flex items-center gap-1 shrink-0">
                 <button 
                   onClick={handleToggleIdentity}
                   title="Switch Identity Mode"
                   className={`p-2 rounded-full transition-colors ${identityMode === 'shadow' ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30' : 'bg-[#0BD9E5]/20 text-[#0BD9E5] hover:bg-[#0BD9E5]/30'}`}
                 >
                    <RefreshCw size={16} />
                 </button>
                 <button 
                   onClick={() => setShowProfileMenu(!showProfileMenu)}
                   className="text-gray-400 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
                 >
                    <MoreHorizontal size={18} />
                 </button>
              </div>
           </div>
        </div>
      </aside>

      {/* MOBILE BOTTOM NAVIGATION BAR */}
      {!hideBottomBar && (
      <nav className={`sm:hidden fixed bottom-0 left-0 right-0 h-[60px] bg-black/90 backdrop-blur-md border-t border-gray-800 flex items-center justify-around px-2 z-50 transition-transform duration-300 ${!isBottomBarVisible ? 'translate-y-full' : 'translate-y-0'}`}>
        
        {/* Home */}
        <button className="flex flex-col items-center gap-0.5 p-1.5 min-w-[48px]" onClick={() => handleFeatureClick('home')}>
           <Home size={24} className={currentPath === '/' ? "text-white" : "text-gray-500"} fill={currentPath === '/' ? "white" : "none"} />
        </button>

        {/* Messages */}
        <button className="flex flex-col items-center gap-0.5 p-1.5 min-w-[48px]" onClick={() => handleFeatureClick('chat')}>
           <MessageSquare size={24} className={currentPath.includes('/chat') ? "text-white" : "text-gray-500"} />
        </button>

        {/* + Post (Center Action) */}
        <button 
          className="flex items-center justify-center w-12 h-12 rounded-full bg-[#0BD9E5] shadow-lg shadow-[#0BD9E5]/20 -mt-4 transition-transform active:scale-90"
          onClick={() => navigate('/create-shadow')}
        >
           <Plus size={28} className="text-black stroke-[2.5]" />
        </button>

        {/* Voices */}
        <div className="relative flex flex-col items-center z-[100]">
          <button className="flex flex-col items-center gap-0.5 p-1.5 min-w-[48px]" onClick={() => setShowVoiceMenu(!showVoiceMenu)}>
             <Mic size={24} className={currentPath.includes('/voice1v1') || currentPath.includes('/voice4v4') || currentPath.includes('/custom-rooms') ? "text-white" : "text-gray-400"} />
          </button>
          
          <AnimatePresence>
            {showVoiceMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowVoiceMenu(false)} />
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95, y: 10 }} 
                  animate={{ opacity: 1, scale: 1, y: 0 }} 
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  className="absolute bottom-full right-[-12px] sm:right-auto sm:left-1/2 sm:-translate-x-1/2 mb-3 bg-[#111] border border-gray-800 rounded-2xl shadow-[0_0_40px_rgba(0,0,0,0.9)] py-2 w-48 z-50 overflow-hidden"
                >
                  <button onClick={() => { setShowVoiceMenu(false); toast.info("Coming soon!"); }} className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center justify-between font-bold text-white text-[15px] group">
                    <div className="flex items-center gap-3">
                      <Mic size={18} className="text-[#0BD9E5] group-hover:scale-110 transition-transform" /> 1-1 Room
                    </div>
                    <Sparkles size={14} className="text-[#0BD9E5]" />
                  </button>
                  <button onClick={() => { setShowVoiceMenu(false); toast.info("Coming soon!"); }} className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center justify-between font-bold text-white text-[15px] group">
                    <div className="flex items-center gap-3">
                      <Users size={18} className="text-[#0BD9E5] group-hover:scale-110 transition-transform" /> 4-4 Room
                    </div>
                    <Sparkles size={14} className="text-[#0BD9E5]" />
                  </button>
                  <button onClick={() => { setShowVoiceMenu(false); handleFeatureClick('customVoice'); }} className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center justify-between font-bold text-white text-[15px] group">
                    <div className="flex items-center gap-3">
                      <PlusCircle size={18} className="text-[#0BD9E5] group-hover:scale-110 transition-transform" /> Custom Voice
                    </div>
                  </button>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* Profile */}
        <button className="flex flex-col items-center gap-0.5 p-1.5 min-w-[48px]" onClick={() => handleFeatureClick('profile')}>
           <User size={24} className={currentPath.includes('/shadow-profile') ? "text-white" : "text-gray-500"} />
        </button>
      </nav>
      )}

      {/* MAIN CONTENT AREA */}
      <RightPanelProvider>
        <AppLayoutInner 
          showDefaultRightPanel={showDefaultRightPanel}
          isActiveCall={isActiveCall}
          isChatPage={isChatPage}
          isSettings={isSettings}
          userProfile={userProfile}
          setShowPremiumModal={setShowPremiumModal}
          navigate={navigate}
          activeCustomRooms={activeCustomRooms}
        />

      </RightPanelProvider>

      {/* MODALS */}
      <AnimatePresence>
        {showAppModal && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#5b708366]"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              className="bg-black border border-gray-800 p-8 rounded-2xl w-full max-w-sm relative shadow-2xl text-center"
            >
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-800 bg-[#16181c]">
                 <Settings className="text-[#0BD9E5] w-8 h-8" />
              </div>
              <h3 className="text-2xl font-extrabold text-white mb-2">{modalConfig.title}</h3>
              <p className="text-gray-400 mb-6 text-[15px] leading-relaxed text-center">
                {modalConfig.description}
              </p>
              <div className="flex flex-col gap-3">
                <a href="https://play.google.com/store/apps/details?id=com.sparkthegenzapp" target="_blank" rel="noopener noreferrer" className="bg-white text-black font-bold py-3.5 px-8 rounded-full hover:bg-gray-200 transition-colors w-full flex items-center justify-center gap-2">
                  Download App <ArrowRight size={18} />
                </a>
                <button onClick={() => setShowAppModal(false)} className="bg-transparent border border-gray-700 text-white font-bold py-3 rounded-full hover:bg-[#16181c] transition-colors mt-2">
                  Maybe Later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <PremiumModal isOpen={showPremiumModal} onClose={() => setShowPremiumModal(false)} />
      <PremiumWelcomeModal isOpen={showWelcomeModal} onClose={handleCloseWelcomeModal} />
      
      <ServiceModal 
        visible={activeServiceMode === 'TERMINATED'} 
        type="TERMINATED"
        onClose={() => {
          setActiveServiceMode(null);
          signOut(auth);
        }}
      />
    </div>
  );
};

const AppLayoutInner = ({ showDefaultRightPanel, isActiveCall, isChatPage, isSettings, userProfile, setShowPremiumModal, navigate, activeCustomRooms = [] }) => {
  const { isRightPanelOpen } = useRightPanel();

  return (
    <>
      <Outlet context={{ setShowPremiumModal }} />

      {/* RIGHT SIDEBAR */}
      {showDefaultRightPanel ? (
        <aside className="hidden lg:flex flex-col w-[350px] pl-8 py-3 pr-2 h-screen sticky top-0 gap-4 overflow-y-auto overflow-x-hidden">
            {/* Subscribe Premium Card */}
            {userProfile?.isPremium ? null : (
              <div className="bg-[#16181c] rounded-2xl p-4 border border-gray-800 mr-2">
                 <h3 className="font-extrabold text-[20px] leading-tight mb-2">Subscribe to Premium</h3>
                 <p className="text-[15px] leading-tight mb-4 text-white">Join the Creator Program, unlock Voices, get early access & more.</p>
                 <button 
                    onClick={() => toast.info("Coming soon!")}
                    className="bg-[#0BD9E5] hover:bg-[#09bcc7] text-black font-bold py-1.5 px-4 rounded-full transition-colors text-[15px]"
                 >
                    Subscribe
                 </button>
              </div>
            )}
  
            {/* Live Voice Rooms */}
          <div className="bg-[#16181c] rounded-2xl pt-4 border border-gray-800/80 flex flex-col mr-2 shadow-lg shadow-black/40 overflow-hidden">
             <div className="flex items-center justify-between px-4 mb-4">
                <div className="flex items-center gap-2">
                   <h3 className="font-extrabold text-[19px] tracking-tight text-white">Live Voice Rooms</h3>
                   <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#0BD9E5]/15 border border-[#0BD9E5]/30">
                      <Mic size={15} className="text-[#0BD9E5] animate-pulse" />
                   </div>
                </div>
                <span className="flex items-center gap-1 text-[11px] font-bold text-[#0BD9E5] uppercase tracking-wider bg-[#0BD9E5]/10 px-2 py-0.5 rounded-full border border-[#0BD9E5]/20">
                   <span className="w-1.5 h-1.5 rounded-full bg-[#0BD9E5] animate-ping" /> Live
                </span>
             </div>
             
             {activeCustomRooms.length > 0 ? (
               activeCustomRooms.map((room) => (
                 <div 
                   key={room.id} 
                   onClick={() => navigate('/custom-rooms')}
                   className="px-4 py-3 hover:bg-white/5 cursor-pointer transition-colors flex justify-between"
                 >
                    <div>
                      <div className="text-[13px] text-gray-500 flex items-center gap-1">
                        <Users size={12} className="text-gray-500" />
                        {room.participantUids?.length || 0}/{room.maxSize || 8} • Voice Room
                        {room.status === 'Ended' ? (
                            <span className="ml-1 text-red-500 font-bold uppercase text-[10px]">ENDED</span>
                        ) : (room.participantUids?.length || 0) >= (room.maxSize || 8) ? (
                            <span className="ml-1 text-red-400 font-bold uppercase text-[10px]">FULL</span>
                        ) : null}
                      </div>
                      <div className="font-bold text-[15px] mt-0.5 line-clamp-1">{room.name}</div>
                    </div>
                    <ArrowRight size={18} className="text-gray-500 hover:text-[#0BD9E5] mt-1 shrink-0 ml-2 transition-colors" />
                 </div>
               ))
             ) : (
               [
                 { topic: 'Trending in College', tag: '#FinalsWeek', posts: '14.2K posts' },
                 { topic: 'Greek Life · Trending', tag: '#Rush2026', posts: '8,432 posts' },
                 { topic: 'Sports · Trending', tag: 'Game Day', posts: '22.1K posts' },
               ].map((trend, i) => (
                 <div key={i} className="px-4 py-3 hover:bg-white/5 cursor-pointer transition-colors flex justify-between">
                    <div>
                      <div className="text-[13px] text-gray-500">{trend.topic}</div>
                      <div className="font-bold text-[15px] mt-0.5">{trend.tag}</div>
                      <div className="text-[13px] text-gray-500 mt-0.5">{trend.posts}</div>
                    </div>
                    <MoreHorizontal size={18} className="text-gray-500 hover:bg-[#0BD9E5]/10 hover:text-[#0BD9E5] rounded-full p-1" />
                 </div>
               ))
             )}
             <div onClick={() => navigate('/custom-rooms')} className="px-4 py-4 text-[#0BD9E5] hover:bg-white/5 cursor-pointer rounded-b-2xl transition-colors text-[15px]">
                Show more
             </div>
          </div>

          <div className="text-[13px] text-gray-500 px-4 flex flex-col gap-y-1 mt-2 mb-20 w-[calc(100%-8px)]">
             <span>&copy; 2026 Spark Platforms, Inc.</span>
             <div className="flex flex-wrap gap-x-3 mt-1">
                 <a href="https://www.sparkpl.com/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="hover:underline">Privacy Policy</a>
                 <a href="https://www.sparkpl.com/terms-and-conditions.html" target="_blank" rel="noopener noreferrer" className="hover:underline">Terms of Service</a>
                 <a href="https://www.sparkpl.com/child-safety-standards.html" target="_blank" rel="noopener noreferrer" className="hover:underline">Child Safety Policy</a>
             </div>
          </div>
        </aside>
      ) : isActiveCall ? (
        /* During active calls, always render container, toggle width */
        <aside className={`hidden lg:flex flex-col h-screen sticky top-0 transition-all duration-300 ${
            isRightPanelOpen ? 'w-[350px] border-l border-gray-800 bg-black' : 'w-0 border-transparent overflow-hidden'
        }`}>
          <div id="right-panel-chat-container" className="flex-1 w-full h-full relative min-w-[350px]">
            {/* The Chat component will be portaled here */}
          </div>
        </aside>
      ) : !isChatPage && !isSettings ? (
        <aside className="hidden lg:flex flex-col w-[350px] pl-8 py-3 pr-2 h-screen sticky top-0"></aside>
      ) : null}
    </>
  );
};
